// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SubReportLink.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import uiState from 'logic/Izenda.UiState';
import config from 'Izenda.Config';
import {
  map,
  pickBy,
  startsWith,
  toLower,
  replace,
  flow,
  forEach,
  filter,
  assign,
  reject,
  get,
  isNil,
  groupBy,
  flatMap,
  concat,
  compact,
  join,
  set,
  merge,
  isUndefined,
  omitBy,
  forOwn,
  find,
  isEmpty
} from 'lodash/fp';
import { DEFAUFT_VALUE as DEFAULT_FILTER_VALUE } from 'logic/data/Izenda.FilterDetailsConfigConstants';
import { FILTER_URL } from 'logic/data/Izenda.ReportDesignerConstants';
const OVERIDING_FILTER_VALUE_REGEX = /^m?p(\d+)value$/;
const OVERIDING_FILTER_LOOKUP_REGEX = FILTER_URL.OVERIDE_MASTER_REPORT_USE_USER_LOOKUP.REGEX;
// Use lodash version with support key
const convertSetting = { cap: false };

const readSubReportQuery = function(query: string) {
  const mapWithKey = map.convert(convertSetting);
  const pickByWithKey = pickBy.convert(convertSetting);

  const pickPropertyStartWithFilters = pickByWithKey((value, key) => {
    return startsWith('filters', toLower(key));
  });

  const mapObjectToArray = mapWithKey((value, key) => {
    return {
      key: toLower(replace(/^filters(\[[0-9]+\])?_/, '', key)),
      value,
      linkReportFilter: true
    };
  });

  let overridingFilterValue = {};

  const filters = flow(
    pickPropertyStartWithFilters,
    mapObjectToArray,
    forEach(filter => {
      OVERIDING_FILTER_VALUE_REGEX.test(toLower(filter.key)) &&
        (overridingFilterValue[toLower(filter.key)] = filter.value);
    }),
    filter(filter => !OVERIDING_FILTER_VALUE_REGEX.test(toLower(filter.key)))
  )(query);

  let subReportFilter = {
    filters,
    overridingFilterValue
  };

  if (query.masterReportId) {
    subReportFilter = assign(subReportFilter, {
      masterReportId: query.masterReportId,
      inheritMasterReportFilter: true
    });
  }

  return subReportFilter;
};

const buildSubReportQuery = function(filters: Array<any>, masterReportId: string) {
  const removeFiltersWithKeyIsNil = reject(
    flow(
      get('key'),
      isNil
    )
  );
  const groupFiltersBySameKey = groupBy('key');
  const mapFiltersToStringArray = flatMap(sameKeyFilters => {
    if (sameKeyFilters.length === 1) {
      const uniqueFilter = sameKeyFilters[0];
      return [`filters_${encodeURIComponent(toLower(uniqueFilter.key))}=${encodeURIComponent(uniqueFilter.value)}`];
    }

    return sameKeyFilters.map((filter, index) => {
      return `filters[${index}]_${encodeURIComponent(toLower(filter.key))}=${encodeURIComponent(filter.value)}`;
    });
  });

  const filterPartSection = flow(
    removeFiltersWithKeyIsNil,
    groupFiltersBySameKey,
    mapFiltersToStringArray
  )(filters);

  const masterReportSection = masterReportId ? `masterReportId=${masterReportId}` : '';

  return `?${flow(
    concat(filterPartSection),
    compact,
    join('&')
  )([masterReportSection])}`;
};

/**
 * Convert parameter to Query Object that React Router is able understand
 * @param  {[type]} object
 * Ex:
 * {
 *  masterReportId: '',
    inheritMasterReportFilter : true/false,
    // filter based on master report field mapping
    masterReportPartId: '',
    // container name
    containerName: '',
    fieldsMapping: [{
      // field mapping 2 of item with position 1 in container
      key: 'fm1_2',
      value: 'a',
      originalValue: 'b',
      dataFormat: 'c',
      linkReportFilter: true
    }],
    filters: {
      // filter based on report part element alias
      key: fieldNameAlias,
      value: 'a',
    }],
    overridingFilterValue:{
      // overide value of filter 1 in sub report
      // p(\d+)value : p1

      p1value: 'a',
      // overide value of filter 2 in master report
      // mp(\d+)value
      mp2value: 'b'
  * }
  * @return {[type]}                           [object]
  * Ex:
  *{
      masterReportId: 123412431234,
      inheritFilter: true,
      masterReportPartId: 1243123123,
      containerName: 'containerName',
      fm1_2_value: 'a',
      fm1_2_originalValue: 'b',
      fm1_2_dataFormat: 'c',
      field_fieldNameAlias: 'a',
      p1value: 'a',
      mp2value: 'b'
  * }
 */

const convertFilterToQuery = function(param: any) {
  if (!param) return null;

  const {
    masterReportId,
    inheritMasterReportFilter: inheritFilter,
    masterReportPartId,
    containerName,
    fieldsMapping,
    filters,
    overridingFilterValue
  } = param;

  let masterQueryObject = {
    masterReportId,
    inheritFilter,
    masterReportPartId,
    containerName,
    tenantId: uiState.tenantId
  };

  const pickProps = ['value', 'originalValue', 'dataFormat', 'function', 'dataType'];

  forEach(fieldMapping => {
    const { key } = fieldMapping;
    let query = {};

    pickProps.forEach(prop => {
      const objProp = `${key}_${prop}`;
      let propsValue = get(prop, fieldMapping);

      // For Angular, the URL will be decode by default
      // Need to encode params value 2 times to make sure it value still decode when save to Izenda Routing path
      if (!config.NeedToEncodeUrl && prop === 'value') {
        propsValue = encodeURIComponent(propsValue);
      }

      query = set(objProp, propsValue, query);
    });

    masterQueryObject = merge(masterQueryObject, query);
  }, fieldsMapping);

  forEach(filter => {
    const { key, value } = filter;
    const fieldAliasProperty = `field_${key}`;

    masterQueryObject[fieldAliasProperty] = value;
  }, filters);

  masterQueryObject = merge(masterQueryObject, overridingFilterValue);
  masterQueryObject = omitBy(isUndefined, masterQueryObject);

  return masterQueryObject;
};

const convertQueryToFilter = function(queryObject: any) {
  if (!queryObject) return null;

  const masterReportId = queryObject.masterReportId || undefined;
  const masterReportPartId = queryObject.masterReportPartId || undefined;
  const containerName = queryObject.containerName || undefined;
  const { inheritFilter } = queryObject;

  let filterObject = {
    masterReportId,
    masterReportPartId,
    containerName,
    inheritMasterReportFilter: inheritFilter === 'true' || undefined
  };

  let fieldsMapping = [];
  let filters = [];
  let overridingFilterValue = {};
  const forOwnWithKey = forOwn.convert(convertSetting);
  const regexFm = /^(fm(\d+)_(\d+))_(value|originalvalue|dataformat|function|datatype)$/;
  const regexField = /^(field)_(.*)/;

  forOwnWithKey((value, prop) => {
    // Convert 'null' to filter null value
    value = value === 'null' ? DEFAULT_FILTER_VALUE.NULL : value;

    if (OVERIDING_FILTER_LOOKUP_REGEX.test(toLower(prop))) {
      overridingFilterValue[toLower(prop)] = !(/false/.test(toLower(value)));
    }

    if (OVERIDING_FILTER_VALUE_REGEX.test(toLower(prop))) {
      overridingFilterValue[toLower(prop)] = value;
    }

    if (regexField.test(prop)) {
      const arr = regexField.exec(toLower(prop));
      filters.push({
        key: arr[2],
        value
      });
    }

    if (masterReportPartId && containerName && masterReportId && regexFm.test(toLower(prop))) {
      const arr = regexFm.exec(toLower(prop));
      const key = arr[1];
      const name = arr[4];

      let filterObj = find(fieldMapping => fieldMapping.key === key, fieldsMapping);

      if (!filterObj) {
        fieldsMapping.push({
          key,
          linkReportFilter: true,
          [name]: value
        });
      } else {
        filterObj[name] = value;
      }
    }
  }, queryObject);

  const checkEmpty = collection => (isEmpty(collection) ? undefined : collection);

  filterObject.filters = checkEmpty(filters);
  filterObject.fieldsMapping = checkEmpty(fieldsMapping);
  filterObject.overridingFilterValue = checkEmpty(overridingFilterValue);

  return omitBy(isUndefined, filterObject);
};

export default {
  readSubReportQuery,
  buildSubReportQuery,
  convertFilterToQuery,
  convertQueryToFilter
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.SubReportLink.js